/**
 * CompanionCard — the companion's "noticed something" note as a small card
 * (Health declutter 2026-09-09, option D; platform-wide 2026-09-10).
 *
 * Rendered by a page's own callout component, which owns the patterns and the
 * actions; this shell owns the shape so five pages cannot drift on it:
 *   - label in sentence case — `Maya · noticed something` — never the shouted
 *     mono eyebrow (Victor, 2026-09-09);
 *   - `card` layout stacks for the right column (`PageWithAside`), `inline`
 *     keeps the older single-row shape;
 *   - a page renders it only when there is something to say. The full-width
 *     "thinking partner" card duplicated the floating widget and is gone;
 *   - **the pager** (ui-ux-guideline §2.9 rule 6, 2026-09-18): a note that
 *     holds more than one sentence pages through them with two chevron
 *     buttons named Previous / Next and a "2 / 4" counter at the top-right of
 *     the card, wrapping at both ends. "Show me another" — a ghost button in
 *     the action row beside the sentence's own actions — said less than the
 *     counter does and is retired. The sentence is `aria-live="polite"` so a
 *     screen reader hears the next one.
 *
 * The default label reads the family's companion name through a child
 * component, so a caller that passes its own label (the staff home's
 * "Williams · noticed something") never touches the persona preferences.
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Shield, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { COARSE_HIT_28 } from '@/lib/touch-target';
import { useCompanionName } from '@/hooks/useCompanionName';

export interface CalloutPager {
  /** Zero-based index of the sentence shown. */
  index: number;
  count: number;
  onPrev: () => void;
  onNext: () => void;
}

/**
 * The one pager state for a callout: an index that wraps in both directions,
 * and the `pager` prop to hand `CompanionCard` — `undefined` while there is
 * only one thing to say, so the chevrons never render for a single sentence.
 */
export function usePager(count: number): { index: number; pager: CalloutPager | undefined } {
  const [raw, setRaw] = useState(0);
  const index = count > 0 ? ((raw % count) + count) % count : 0;
  const pager: CalloutPager | undefined =
    count > 1
      ? {
          index,
          count,
          onPrev: () => setRaw((i) => i - 1),
          onNext: () => setRaw((i) => i + 1),
        }
      : undefined;
  return { index, pager };
}

export interface CompanionCardProps {
  /** Defaults to `${companionName} · noticed something`. */
  label?: string;
  body: string;
  /** Action buttons and links, rendered in a wrapping row. */
  children?: ReactNode;
  layout?: 'inline' | 'card';
  icon?: LucideIcon;
  /** Present only when there is more than one sentence (see `usePager`). */
  pager?: CalloutPager;
  'data-testid'?: string;
  className?: string;
}

function DefaultLabel() {
  const companionName = useCompanionName();
  const { t } = useTranslation('companion');
  return <>{t('callout.noticed', { companion: companionName })}</>;
}

// #2637 (D-2420): the arrows draw 28 px; on a coarse pointer an invisible
// ::after reaches 8 px past every edge (44 px) at -z-10, under the pager
// group's `isolate` context. The "1 / 3" counter between them is in-flow and
// would paint over a reach that touched it, so a coarse pointer spaces it
// 16 px from each arrow: the reaches stop 8 px short of the counter, and a tap
// 20 px from an arrow's centre lands in the gap, on the arrow's own reach.
const PAGER_BUTTON = cn(
  'inline-grid h-7 w-7 place-items-center rounded-md text-stevens-ink-3 transition-colors hover:bg-stevens-indigo/10 hover:text-stevens-indigo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stevens-indigo/40',
  COARSE_HIT_28,
);

export function CompanionCard({
  label,
  body,
  children,
  layout = 'card',
  icon: Icon = Shield,
  pager,
  className,
  ...rest
}: CompanionCardProps) {
  const { t } = useTranslation(['companion', 'common']);
  const card = layout === 'card';
  const paged = pager != null && pager.count > 1;
  return (
    <div
      data-testid={rest['data-testid']}
      className={cn(
        'rounded-lg border border-stevens-indigo/15 bg-stevens-indigo/5',
        card ? 'p-4' : 'px-4 py-3.5',
        className,
      )}
    >
      <div className={cn('flex gap-3', card ? 'flex-col' : 'items-start')}>
        <span className="flex-shrink-0 mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-stevens-indigo/10 text-stevens-indigo">
          <Icon className="h-3.5 w-3.5" aria-hidden />
        </span>
        <div className="flex-1 min-w-0">
          <div className="mb-1 flex items-center gap-2">
            <p className="m-0 flex-1 min-w-0 text-caption font-medium text-stevens-indigo">
              {label ?? <DefaultLabel />}
            </p>
            {paged && (
              <div className="isolate -mr-1 -mt-1 inline-flex flex-shrink-0 items-center gap-0.5 [@media(pointer:coarse)]:gap-4" role="group" aria-label={t('pager.moreNotes')}>
                <button type="button" onClick={pager.onPrev} aria-label={t('common:actions.previous')} className={PAGER_BUTTON}>
                  <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
                </button>
                <span className="min-w-[3ch] text-center text-caption tabular-nums text-stevens-ink-3" aria-hidden>
                  {pager.index + 1} / {pager.count}
                </span>
                <button type="button" onClick={pager.onNext} aria-label={t('common:actions.next')} className={PAGER_BUTTON}>
                  <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                </button>
              </div>
            )}
          </div>
          <p
            className="font-serif text-lede text-foreground/90 leading-snug mb-3"
            style={{ fontStyle: 'italic', fontWeight: 300 }}
            aria-live={paged ? 'polite' : undefined}
          >
            “{body}”
          </p>
          {children && <div className="flex flex-wrap gap-2">{children}</div>}
        </div>
      </div>
    </div>
  );
}
